import { BASE_BULLMQ_CONFIG } from "@okf/ootils";

/**
 * Builds a synthetic segByImportIdJobs payload that mirrors the shape produced
 * by okf-sub's SocketIOService.calculateSegByImportIdJobs, so
 * QueueJobsProgressFeedback can render every visual state without a real
 * import running.
 *
 * Used internally by QueueJobsProgressFeedback when its `testMode` prop is set.
 *
 * Recognised states (4 — failures are NOT a state, they're an injectable):
 *   - "importing"       → DIRECT_DATA_IMPORT mid-flight (50%), no AI queues yet
 *   - "imported"        → DIRECT_DATA_IMPORT settled, AI queues queued (0%)
 *   - "aiGettingReady"  → DIRECT_DATA_IMPORT settled, AI queues mid-flight (50%)
 *   - "aiReady"         → everything 100%
 *
 * Optional `failedPercent` (0-100) injects failures into whichever state was
 * picked. With failedPercent > 0:
 *   - In "importing": some pending becomes failed (completed stays at ~40%)
 *   - In settled states: completed becomes (100 - failedPercent), failed becomes
 *     failedPercent — i.e. data settled with partial success
 *
 * Usage:
 *   <QueueJobsProgressFeedback
 *     testMode={{ contentType: "x", state: "imported", tplTitle: "Articles", failedPercent: 20 }}
 *   />
 */
export const buildSyntheticSegByImportIdJobs = ({
  contentType,
  state,
  failedPercent = 0,
}) => {
  const baseQueue = (overrides) => ({
    totalItems: 100,
    completed: { count: 0, percent: 0 },
    failed: { count: 0, percent: 0 },
    waiting: { count: 0, percent: 0 },
    active: { count: 0, percent: 0 },
    delayed: { count: 0, percent: 0 },
    actualPercentCompleted: 0,
    ...overrides,
  });

  // direct-import data — varies by state and failedPercent
  const directImport = (() => {
    if (state === "importing") {
      // mid-flight: completed=40, failed=injected, pending=rest
      const completed = failedPercent > 0 ? 40 : 50;
      const pending = 100 - completed - failedPercent;
      return baseQueue({
        completed: { count: completed, percent: completed },
        failed: { count: failedPercent, percent: failedPercent },
        active: { count: pending, percent: pending },
        actualPercentCompleted: completed,
        status: "importingData",
        runIdx: 0,
      });
    }
    // imported / aiGettingReady / aiReady — direct-import is settled
    const completed = 100 - failedPercent;
    return baseQueue({
      completed: { count: completed, percent: completed },
      failed: { count: failedPercent, percent: failedPercent },
      actualPercentCompleted: 100,
      status: "importCompleted",
      runIdx: 0,
    });
  })();

  // AI queue data
  const aiQueue =
    state === "aiReady"
      ? baseQueue({
          completed: { count: 100, percent: 100 },
          actualPercentCompleted: 100,
          runIdx: 3,
        })
      : state === "aiGettingReady"
      ? baseQueue({
          completed: { count: 50, percent: 50 },
          active: { count: 50, percent: 50 },
          actualPercentCompleted: 50,
          runIdx: 3,
        })
      : baseQueue({
          waiting: { count: 100, percent: 100 },
          actualPercentCompleted: 0,
          runIdx: 3,
        });

  // AI queues only land on the wire after data import begins completing —
  // for "importing" we omit them so it matches reality.
  const queues = {
    [BASE_BULLMQ_CONFIG.DIRECT_DATA_IMPORT_QUEUE.id]: directImport,
  };
  if (state !== "importing") {
    queues[BASE_BULLMQ_CONFIG.CHUNKS_ELASTIC_SYNC_QUEUE.id] = aiQueue;
  }

  const importId = `synthetic-${state}-${failedPercent}-${contentType}`;
  return {
    [importId]: {
      importId,
      contentType,
      importTotalCount: 100,
      activityDate: new Date().toISOString(),
      queues,
    },
  };
};
